// The ornament library (canvas v2): curated single-colour vector ornaments —
// frames, corners, dividers, laurels, crests, crowns, seals, ribbons,
// monogram rings and motifs — added to a label as `OrnamentElement`s and
// recoloured to a palette colour.
//
// Assets: app/frontend/assets/ornaments/<group>/<key>.svg, bundled by Vite as
// raw strings (`?raw`) into both the client and the SSR bundle — nothing is
// fetched at runtime. Every file is single colour through `currentColor`
// (fill and stroke), has a tidy viewBox and no text, script, image, id or
// external reference; `test/lib/ornaments_manifest_test.rb` enforces that
// and that each file is listed in LICENCES.md with a permitted licence.
//
// SSR: module scope only holds strings and numbers. Image decoding (Blob,
// URL.createObjectURL, Image) happens only inside functions, which are
// called from effects, handlers and render calls in the browser.

import type { CanvasData } from "./schema";

import hairlineFrameSvg from "@/assets/ornaments/frames/hairline-frame.svg?raw";
import doubleRuleFrameSvg from "@/assets/ornaments/frames/double-rule-frame.svg?raw";
import notchedFrameSvg from "@/assets/ornaments/frames/notched-frame.svg?raw";
import roundedFrameSvg from "@/assets/ornaments/frames/rounded-frame.svg?raw";
import victorianCornersSvg from "@/assets/ornaments/corners/victorian-corners.svg?raw";
import nouveauCornersSvg from "@/assets/ornaments/corners/nouveau-corners.svg?raw";
import leafScrollSvg from "@/assets/ornaments/corners/leaf-scroll.svg?raw";
import simpleSwashSvg from "@/assets/ornaments/corners/simple-swash.svg?raw";
import diamondDividerSvg from "@/assets/ornaments/dividers/diamond-divider.svg?raw";
import leafDividerSvg from "@/assets/ornaments/dividers/leaf-divider.svg?raw";
import filigreeDividerSvg from "@/assets/ornaments/dividers/filigree-divider.svg?raw";
import dottedDividerSvg from "@/assets/ornaments/dividers/dotted-divider.svg?raw";
import laurelPairSvg from "@/assets/ornaments/laurels/laurel-pair.svg?raw";
import closedWreathSvg from "@/assets/ornaments/laurels/closed-wreath.svg?raw";
import oliveBranchSvg from "@/assets/ornaments/laurels/olive-branch.svg?raw";
import oakLeavesSvg from "@/assets/ornaments/laurels/oak-leaves.svg?raw";
import heaterShieldSvg from "@/assets/ornaments/crests/heater-shield.svg?raw";
import frenchShieldSvg from "@/assets/ornaments/crests/french-shield.svg?raw";
import cartoucheSvg from "@/assets/ornaments/crests/cartouche.svg?raw";
import mantledShieldSvg from "@/assets/ornaments/crests/mantled-shield.svg?raw";
import openCrownSvg from "@/assets/ornaments/crowns/open-crown.svg?raw";
import laurelCrownSvg from "@/assets/ornaments/crowns/laurel-crown.svg?raw";
import coronetSvg from "@/assets/ornaments/crowns/coronet.svg?raw";
import tiaraSvg from "@/assets/ornaments/crowns/tiara.svg?raw";
import waxSealSvg from "@/assets/ornaments/seals/wax-seal.svg?raw";
import medallionRingSvg from "@/assets/ornaments/seals/medallion-ring.svg?raw";
import scallopedSealSvg from "@/assets/ornaments/seals/scalloped-seal.svg?raw";
import rosetteSvg from "@/assets/ornaments/seals/rosette.svg?raw";
import straightRibbonSvg from "@/assets/ornaments/ribbons/straight-ribbon.svg?raw";
import curvedBannerSvg from "@/assets/ornaments/ribbons/curved-banner.svg?raw";
import pennantRibbonSvg from "@/assets/ornaments/ribbons/pennant-ribbon.svg?raw";
import doubleRibbonSvg from "@/assets/ornaments/ribbons/double-ribbon.svg?raw";
import doubleRingSvg from "@/assets/ornaments/monograms/double-ring.svg?raw";
import dottedRingSvg from "@/assets/ornaments/monograms/dotted-ring.svg?raw";
import hexagonRingSvg from "@/assets/ornaments/monograms/hexagon-ring.svg?raw";
import wreathRingSvg from "@/assets/ornaments/monograms/wreath-ring.svg?raw";
import grapeBunchSvg from "@/assets/ornaments/motifs/grape-bunch.svg?raw";
import vineLeafSvg from "@/assets/ornaments/motifs/vine-leaf.svg?raw";
import sunburstSvg from "@/assets/ornaments/motifs/sunburst.svg?raw";
import decoFanSvg from "@/assets/ornaments/motifs/deco-fan.svg?raw";
import fleurDeLisSvg from "@/assets/ornaments/motifs/fleur-de-lis.svg?raw";
import starSvg from "@/assets/ornaments/motifs/star.svg?raw";

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export type OrnamentGroup =
  | "frames"
  | "corners"
  | "dividers"
  | "laurels"
  | "crests"
  | "crowns"
  | "seals"
  | "ribbons"
  | "monograms"
  | "motifs";

/** The ten groups, in menu order. */
export const ORNAMENT_GROUPS: readonly { key: OrnamentGroup; label: string }[] = [
  { key: "frames", label: "Frames & borders" },
  { key: "corners", label: "Corners & flourishes" },
  { key: "dividers", label: "Dividers" },
  { key: "laurels", label: "Laurels & wreaths" },
  { key: "crests", label: "Crests & shields" },
  { key: "crowns", label: "Crowns & coronets" },
  { key: "seals", label: "Seals & medallions" },
  { key: "ribbons", label: "Ribbons & banners" },
  { key: "monograms", label: "Monogram rings" },
  { key: "motifs", label: "Botanical & motifs" },
] as const;

export type OrnamentLicence = "CC0" | "Public Domain" | "Authored (SOH GD)";

interface OrnamentDef {
  key: string;
  label: string;
  group: OrnamentGroup;
  svg: string;
  /**
   * Open-centred ornaments (frames, corner sets, rings, wreaths, shields)
   * hit-test only near their drawn edge, like an unfilled rectangle frame:
   * a box inset by `hollow × min(width, height)` on every side is click-
   * through, so text or an image slot inside stays selectable. Solid
   * ornaments omit it and hit-test as their whole box.
   */
  hollow?: number;
}

const DEFS = [
  { key: "hairline-frame", label: "Single hairline frame", group: "frames", svg: hairlineFrameSvg, hollow: 0.12 },
  { key: "double-rule-frame", label: "Double rule frame", group: "frames", svg: doubleRuleFrameSvg, hollow: 0.1 },
  { key: "notched-frame", label: "Corner-notched frame", group: "frames", svg: notchedFrameSvg, hollow: 0.12 },
  { key: "rounded-frame", label: "Rounded frame", group: "frames", svg: roundedFrameSvg, hollow: 0.12 },
  { key: "victorian-corners", label: "Victorian corners", group: "corners", svg: victorianCornersSvg, hollow: 0.2 },
  { key: "nouveau-corners", label: "Art nouveau corners", group: "corners", svg: nouveauCornersSvg, hollow: 0.2 },
  { key: "leaf-scroll", label: "Leaf scroll", group: "corners", svg: leafScrollSvg },
  { key: "simple-swash", label: "Simple swash", group: "corners", svg: simpleSwashSvg },
  { key: "diamond-divider", label: "Diamond divider", group: "dividers", svg: diamondDividerSvg },
  { key: "leaf-divider", label: "Leaf divider", group: "dividers", svg: leafDividerSvg },
  { key: "filigree-divider", label: "Filigree divider", group: "dividers", svg: filigreeDividerSvg },
  { key: "dotted-divider", label: "Dotted divider", group: "dividers", svg: dottedDividerSvg },
  { key: "laurel-pair", label: "Open laurel pair", group: "laurels", svg: laurelPairSvg, hollow: 0.18 },
  { key: "closed-wreath", label: "Closed wreath", group: "laurels", svg: closedWreathSvg, hollow: 0.3 },
  { key: "olive-branch", label: "Olive branch", group: "laurels", svg: oliveBranchSvg },
  { key: "oak-leaves", label: "Oak leaves", group: "laurels", svg: oakLeavesSvg },
  { key: "heater-shield", label: "Heater shield", group: "crests", svg: heaterShieldSvg, hollow: 0.22 },
  { key: "french-shield", label: "French shield", group: "crests", svg: frenchShieldSvg, hollow: 0.22 },
  { key: "cartouche", label: "Cartouche", group: "crests", svg: cartoucheSvg, hollow: 0.28 },
  { key: "mantled-shield", label: "Escutcheon with mantling", group: "crests", svg: mantledShieldSvg, hollow: 0.2 },
  { key: "open-crown", label: "Open crown", group: "crowns", svg: openCrownSvg },
  { key: "laurel-crown", label: "Laurel crown", group: "crowns", svg: laurelCrownSvg },
  { key: "coronet", label: "Coronet", group: "crowns", svg: coronetSvg },
  { key: "tiara", label: "Tiara", group: "crowns", svg: tiaraSvg },
  { key: "wax-seal", label: "Wax seal", group: "seals", svg: waxSealSvg },
  { key: "medallion-ring", label: "Medallion ring", group: "seals", svg: medallionRingSvg, hollow: 0.2 },
  { key: "scalloped-seal", label: "Scalloped seal", group: "seals", svg: scallopedSealSvg, hollow: 0.3 },
  { key: "rosette", label: "Rosette", group: "seals", svg: rosetteSvg },
  { key: "straight-ribbon", label: "Straight ribbon", group: "ribbons", svg: straightRibbonSvg },
  { key: "curved-banner", label: "Curved banner", group: "ribbons", svg: curvedBannerSvg },
  { key: "pennant-ribbon", label: "Forked pennant", group: "ribbons", svg: pennantRibbonSvg },
  { key: "double-ribbon", label: "Double ribbon", group: "ribbons", svg: doubleRibbonSvg },
  { key: "double-ring", label: "Double ring", group: "monograms", svg: doubleRingSvg, hollow: 0.34 },
  { key: "dotted-ring", label: "Ring with dots", group: "monograms", svg: dottedRingSvg, hollow: 0.34 },
  { key: "hexagon-ring", label: "Hexagon ring", group: "monograms", svg: hexagonRingSvg, hollow: 0.34 },
  { key: "wreath-ring", label: "Wreath ring", group: "monograms", svg: wreathRingSvg, hollow: 0.34 },
  { key: "grape-bunch", label: "Grape bunch", group: "motifs", svg: grapeBunchSvg },
  { key: "vine-leaf", label: "Vine leaf", group: "motifs", svg: vineLeafSvg },
  { key: "sunburst", label: "Sunburst", group: "motifs", svg: sunburstSvg },
  { key: "deco-fan", label: "Deco fan", group: "motifs", svg: decoFanSvg },
  { key: "fleur-de-lis", label: "Fleur-de-lis", group: "motifs", svg: fleurDeLisSvg },
  { key: "star", label: "Eight-point star", group: "motifs", svg: starSvg },
] as const satisfies readonly OrnamentDef[];

export type OrnamentKey = (typeof DEFS)[number]["key"];

export interface Ornament {
  key: OrnamentKey;
  label: string;
  group: OrnamentGroup;
  /** Width ÷ height of the SVG viewBox. Ornament boxes always keep it. */
  aspect: number;
  /** The SVG viewBox (tight to the drawn ink), in SVG units. */
  viewBox: { x: number; y: number; width: number; height: number };
  /** The raw, sanitised SVG markup (single colour through currentColor). */
  svg: string;
  /** "authored" (drawn in-house) or the source page URL for a downloaded file. */
  source: "authored" | string;
  licence: OrnamentLicence;
  /** See OrnamentDef.hollow; 0 = solid. */
  hollow: number;
}

function parseViewBox(svg: string): Ornament["viewBox"] {
  const m = /viewBox="\s*([-\d.]+)[\s,]+([-\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)\s*"/.exec(svg);
  const [x, y, width, height] = m ? [m[1], m[2], m[3], m[4]].map(Number) : [0, 0, 1, 1];
  return width > 0 && height > 0 ? { x, y, width, height } : { x: 0, y: 0, width: 1, height: 1 };
}

/** Every ornament, in menu order (grouped). All are authored in-house. */
export const ORNAMENTS: readonly Ornament[] = DEFS.map((d) => {
  const viewBox = parseViewBox(d.svg);
  return {
    key: d.key,
    label: d.label,
    group: d.group,
    aspect: viewBox.width / viewBox.height,
    viewBox,
    svg: d.svg,
    source: "authored",
    licence: "Authored (SOH GD)",
    hollow: "hollow" in d ? d.hollow : 0,
  };
});

export const ORNAMENT_KEYS: readonly OrnamentKey[] = ORNAMENTS.map((o) => o.key);

const BY_KEY = new Map<string, Ornament>(ORNAMENTS.map((o) => [o.key, o]));

export function isOrnamentKey(v: unknown): v is OrnamentKey {
  return typeof v === "string" && BY_KEY.has(v);
}

export function getOrnament(key: string): Ornament | null {
  return BY_KEY.get(key) ?? null;
}

export function ornamentsInGroup(group: OrnamentGroup): readonly Ornament[] {
  return ORNAMENTS.filter((o) => o.group === group);
}

// ---------------------------------------------------------------------------
// Markup
// ---------------------------------------------------------------------------

const HEX = /^#[0-9a-f]{6}$/i;

export interface OrnamentMarkupOptions {
  /** Hex colour for currentColor; omit to inherit the CSS `color` (inline use). */
  color?: string;
  /** Root width/height attributes (px number, or a CSS length such as "100%"). */
  width?: number | string;
  height?: number | string;
  /** Adds aria-hidden="true" focusable="false" (default true — previews are decorative). */
  decorative?: boolean;
}

/**
 * The ornament's SVG with attributes added to its root: `color` (so
 * currentColor resolves to the palette colour), `width`/`height`, and
 * aria-hidden for decorative inline use. Safe to inject into the DOM (for the
 * menu previews) because the files are bundled, sanitised by the manifest
 * test and never user-supplied. Empty string for an unknown key.
 */
export function ornamentMarkup(key: string, opts: OrnamentMarkupOptions = {}): string {
  const o = getOrnament(key);
  if (!o) return "";
  const attrs: string[] = [];
  if (opts.width !== undefined) attrs.push(`width="${escapeAttr(String(opts.width))}"`);
  if (opts.height !== undefined) attrs.push(`height="${escapeAttr(String(opts.height))}"`);
  if (opts.color && HEX.test(opts.color)) attrs.push(`color="${opts.color.toLowerCase()}"`);
  if (opts.decorative !== false) attrs.push(`aria-hidden="true" focusable="false"`);
  const svg = o.svg.trim();
  return attrs.length ? svg.replace(/^<svg\b/, `<svg ${attrs.join(" ")}`) : svg;
}

function escapeAttr(v: string): string {
  return v.replace(/[^0-9a-zA-Z.%-]/g, "");
}

// ---------------------------------------------------------------------------
// Decoding for canvas drawing (browser only)
// ---------------------------------------------------------------------------
//
// A canvas cannot draw an SVG string, so each (ornament, colour, size) is
// turned into an SVG Blob URL and decoded with an Image. The root gets
// explicit width/height at the TARGET PIXEL SIZE because Safari rasterises an
// SVG image at its intrinsic size — without them a scaled-up ornament blurs.
// Sizes are bucketed to 64 px steps (rounded up, so the bitmap is never
// upscaled) so a resize drag does not re-decode on every frame. Decoded
// images are cached per key|colour|bucket; the least recently used are
// evicted (and their object URLs revoked) beyond MAX_ENTRIES.

/** Size step of the decode cache, device px. */
export const ORNAMENT_SIZE_BUCKET_PX = 64;
/** Largest decode, device px on the ornament's long side. */
export const ORNAMENT_MAX_DECODE_PX = 4096;
const MAX_ENTRIES = 120;

interface CacheEntry {
  key: string;
  color: string;
  bucket: number;
  img: HTMLImageElement | null;
  url: string | null;
  failed: boolean;
  used: number;
  promise: Promise<HTMLImageElement | null>;
}

const cache = new Map<string, CacheEntry>();
const listeners = new Set<() => void>();
let clock = 0;

function canDecode(): boolean {
  return (
    typeof Image !== "undefined" &&
    typeof Blob !== "undefined" &&
    typeof URL !== "undefined" &&
    typeof URL.createObjectURL === "function"
  );
}

/** The 64 px bucket (long side) a target size decodes at. */
export function ornamentBucket(targetPx: number): number {
  const t = Number.isFinite(targetPx) && targetPx > 0 ? targetPx : ORNAMENT_SIZE_BUCKET_PX;
  const b = Math.ceil(t / ORNAMENT_SIZE_BUCKET_PX) * ORNAMENT_SIZE_BUCKET_PX;
  return Math.min(ORNAMENT_MAX_DECODE_PX, Math.max(ORNAMENT_SIZE_BUCKET_PX, b));
}

const cacheId = (key: string, color: string, bucket: number) => `${key}|${color}|${bucket}`;

function evict(): void {
  if (cache.size <= MAX_ENTRIES) return;
  const ready = [...cache.entries()].filter(([, e]) => e.img || e.failed).sort((a, b) => a[1].used - b[1].used);
  for (const [id, e] of ready) {
    if (cache.size <= MAX_ENTRIES) break;
    if (e.url) URL.revokeObjectURL(e.url);
    cache.delete(id);
  }
}

function notify(): void {
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch {
      /* a listener's failure must not stop the others */
    }
  }
}

/**
 * Decode `key` in `color` for drawing at `targetPx` device px on its long
 * side. Resolves with the decoded image (cached), or null when it cannot be
 * decoded (unknown key, SSR, a decode error). Never rejects.
 */
export function decodeOrnament(key: string, color: string, targetPx: number): Promise<HTMLImageElement | null> {
  const o = getOrnament(key);
  if (!o || !canDecode()) return Promise.resolve(null);
  const hex = HEX.test(color) ? color.toLowerCase() : "#000000";
  const bucket = ornamentBucket(targetPx);
  const id = cacheId(o.key, hex, bucket);
  const hit = cache.get(id);
  if (hit) {
    hit.used = ++clock;
    return hit.promise;
  }
  const w = o.aspect >= 1 ? bucket : Math.max(1, Math.round(bucket * o.aspect));
  const h = o.aspect >= 1 ? Math.max(1, Math.round(bucket / o.aspect)) : bucket;
  const entry: CacheEntry = {
    key: o.key,
    color: hex,
    bucket,
    img: null,
    url: null,
    failed: false,
    used: ++clock,
    promise: Promise.resolve(null),
  };
  entry.promise = new Promise<HTMLImageElement | null>((resolve) => {
    try {
      const markup = ornamentMarkup(o.key, { color: hex, width: w, height: h, decorative: false });
      const url = URL.createObjectURL(new Blob([markup], { type: "image/svg+xml" }));
      entry.url = url;
      const img = new Image();
      img.decoding = "async";
      img.onload = () => {
        const done = () => {
          entry.img = img;
          evict();
          notify();
          resolve(img);
        };
        // decode() guarantees the bitmap is ready before the first drawImage.
        if (typeof img.decode === "function") img.decode().then(done, done);
        else done();
      };
      img.onerror = () => {
        entry.failed = true;
        resolve(null);
      };
      img.src = url;
    } catch {
      entry.failed = true;
      resolve(null);
    }
  });
  cache.set(id, entry);
  return entry.promise;
}

/**
 * The decoded image to draw `key` in `color` at `targetPx`, synchronously.
 * - Returns the exact size bucket when it is ready.
 * - Otherwise starts decoding it and returns the best bitmap already decoded
 *   for that key and colour (the smallest bucket at least as large, else the
 *   largest smaller one), so a resize or zoom never flickers.
 * - Returns null when nothing is ready yet (the editor then draws a dashed
 *   placeholder and redraws when `subscribeOrnaments` fires). Always null
 *   during SSR.
 */
export function ornamentImage(key: string, color: string, targetPx: number): HTMLImageElement | null {
  if (!canDecode()) return null;
  const o = getOrnament(key);
  if (!o) return null;
  const hex = HEX.test(color) ? color.toLowerCase() : "#000000";
  const bucket = ornamentBucket(targetPx);
  const exact = cache.get(cacheId(o.key, hex, bucket));
  if (exact?.img) {
    exact.used = ++clock;
    return exact.img;
  }
  if (!exact) void decodeOrnament(o.key, hex, targetPx);
  let above: CacheEntry | null = null;
  let below: CacheEntry | null = null;
  for (const e of cache.values()) {
    if (!e.img || e.key !== o.key || e.color !== hex) continue;
    if (e.bucket >= bucket) {
      if (!above || e.bucket < above.bucket) above = e;
    } else if (!below || e.bucket > below.bucket) {
      below = e;
    }
  }
  const best = above ?? below;
  if (best) best.used = ++clock;
  return best?.img ?? null;
}

/** Called after any ornament finishes decoding (the editor redraws). Returns an unsubscribe. */
export function subscribeOrnaments(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Print resolution (300 dpi) in px per mm — the default export size for loadOrnaments. */
const PRINT_PX_PER_MM = 300 / 25.4;

/**
 * Pre-decode every ornament `doc` uses, at `pxPerMm` device px per mm (the
 * export's scale; defaults to 300 dpi). Resolves when all have decoded or
 * failed, or after `timeoutMs` — never rejects. No-op during SSR. Exports
 * await this (alongside loadFonts) so they never draw a placeholder.
 */
export async function loadOrnaments(
  doc: Pick<CanvasData, "elements"> | { elements: readonly unknown[] } | null | undefined,
  pxPerMm = PRINT_PX_PER_MM,
  timeoutMs = 4000,
): Promise<void> {
  if (!canDecode() || !doc) return;
  const loads: Promise<unknown>[] = [];
  for (const raw of doc.elements) {
    const el = raw as { type?: unknown; asset?: unknown; color?: unknown; width?: unknown; height?: unknown };
    if (el?.type !== "ornament" || typeof el.asset !== "string" || typeof el.color !== "string") continue;
    const w = typeof el.width === "number" ? el.width : 0;
    const h = typeof el.height === "number" ? el.height : 0;
    loads.push(decodeOrnament(el.asset, el.color, Math.max(w, h) * pxPerMm));
  }
  if (loads.length === 0) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  await Promise.race([Promise.all(loads).then(() => undefined), timeout]);
  if (timer) clearTimeout(timer);
}
